import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import BaseWrapper from "../../components/BaseWrapper/BaseWrapper"
import { FormProvider } from "../../contexts/CreateRecordFormContext"
import Feedback from "../../components/Feedback/Feedback"
import RecordModal from "../../components/RecordModal/RecordModal"
import { Autocomplete, Box, Button, CardContent, CircularProgress, Collapse, IconButton, Stack, TextField, Typography, useMediaQuery, useTheme } from "@mui/material"
import { CloudDownload, Construction, ExpandLess, ExpandMore } from "@mui/icons-material"
import { DataGridPro } from "@mui/x-data-grid-pro"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import QuoteColumns from "../../lib/TableColumns/QuoteColumns"
import { API } from "../../lib/api"
import dayjs from "dayjs"
import isoWeek from "dayjs/plugin/isoWeek"
import weekOfYear from "dayjs/plugin/weekOfYear"
import ReactECharts from "echarts-for-react"
import CollapsibleGridCard from "../../components/CollapsibleGridCard"
import { useThemePreference } from "../../contexts/ThemeContext"
import DailyQuoteTrendCard from "../../components/DailyQuoteTrendCard"
import { GRID_SESSION_KEYS, readGridSessionState } from "../../lib/gridSessionState"

dayjs.extend(isoWeek)
dayjs.extend(weekOfYear)

const formatUSD = (n = 0) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)

const getCurrentWeekRange = () => ({
    start: dayjs().startOf("isoWeek"),
    end: dayjs().endOf("isoWeek"),
})

const tagOptions = [
    { value: "rcu", label: "Rail Car Unload" },
    { value: "fvt", label: "Vacuum Takeoff Kit" },
    { value: "cc-kit", label: "Pressure Convey Kit (CC-KIT)" },
    { value: "monolayer", label: "Mono Layer" },
    { value: "coex", label: "Coex" },
    { value: "3-layer", label: "3-Layer" },
    { value: "4-layer", label: "4-Layer" },
    { value: "5-layer", label: "5-Layer" },
    { value: "7-layer", label: "7-layer" },
    { value: "9-layer", label: "9-layer" },
    { value: "auto-clean", label: "Auto Clean" },
    { value: "rate-control", label: "Rate Control" },
    { value: "smart-stand", label: "Smart Stand" },
    { value: "silo", label: "Silo" },
    { value: "daybin", label: "Daybin" },
    { value: "box-tilt", label: "Box Tilt" },
    { value: "auto-aspirator", label: "Auto Aspirator" },
    { value: "cleanout", label: "Cleanout Station" },
    { value: "cyclone", label: "Cyclone" },
    { value: "auger-feeder", label: "Auger Feeder" },
    { value: "mezz", label: "Mezzanine" },
    { value: "allen-bradley", label: "Allen Bradley" },
    { value: "beckhoff", label: "Beckhoff" },
    { value: "project-addition", label: "Project Addition" },
    { value: "tranquility", label: "Tranquility Pump" },
    { value: "standard-pump", label: "Standard Pump" },
    { value: "pressure-pump", label: "Pressure Pump" },
    { value: "sasquatch", label: "Sasquatch" },
    { value: "dryer", label: "Dryer" },
    { value: "multi-hopper", label: "Multi Hopper" },
    { value: "tm-pump", label: "MK Series TM Pump" },
    { value: "moretto", label: "Moretto" },
    { value: "brabender", label: "Brabender" },
    { value: "schenck", label: "Schenck" },
    { value: "install", label: "Install" },
]

const normalizeSearchState = ({ part = "", tags = [], isActive = false } = {}) => ({
    part: part.trim().toLowerCase(),
    tags,
    isActive,
})

const defaultPaginationModel = { page: 0, pageSize: 15 }
const defaultSortModel = [{ field: "updatedAt", sort: "desc" }]
const defaultFilterModel = { items: [] }

const getSessionUserId = () => typeof localStorage !== "undefined" ? localStorage.getItem("id") : undefined

const getInitialGridState = () =>
    readGridSessionState(GRID_SESSION_KEYS.engineeringQuotingGrid, getSessionUserId()) || {}

const getInitialPaginationModel = () => {
    const paginationModel = getInitialGridState()?.paginationModel
    return paginationModel?.pageSize ? paginationModel : defaultPaginationModel
}

const getInitialSortModel = () => {
    const sortModel = getInitialGridState()?.sortModel
    return Array.isArray(sortModel) && sortModel.length ? sortModel : defaultSortModel
}

const getInitialFilterModel = () => {
    const filterModel = getInitialGridState()?.filterModel
    return filterModel?.items || filterModel?.quickFilterValues ? filterModel : defaultFilterModel
}

const Quoting = () => {
    const { isDarkMode } = useThemePreference()

    const [quotes, setQuotes] = useState([])
    const [gridQuotes, setGridQuotes] = useState([])
    const [quoteCount, setQuoteCount] = useState(0)
    const [collapsedWeekly, setCollapseWeekly] = useState(false)
    const [openUpdateModal, setOpenUpdateModal] = useState(false)
    const [feedback, setFeedback] = useState({})
    const [selectedRows, setSelectedRows] = useState([])
    const [loading, setLoading] = useState(true)
    const [gridLoading, setGridLoading] = useState(false)
    const [selectedTags, setSelectedTags] = useState([])
    const [searchState, setSearchState] = useState(() => normalizeSearchState())
    const [paginationModel, setPaginationModel] = useState(getInitialPaginationModel)
    const [sortModel, setSortModel] = useState(getInitialSortModel)
    const [filterModel, setFilterModel] = useState(getInitialFilterModel)
    const [defaultStartDate, setDefaultStartDate] = useState(() =>
        getCurrentWeekRange().start.format("YYYY-MM-DD")
    )
    const [defaultEndDate, setDefaultEndDate] = useState(() =>
        getCurrentWeekRange().end.format("YYYY-MM-DD")
    )

    const chartRef = useRef()
    const partNumRef = useRef("")
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

    const columns = QuoteColumns()

    const fetchQuotePage = useCallback(async ({ showInitialLoader = false } = {}) => {
        if (showInitialLoader) {
            setLoading(true)
        } else {
            setGridLoading(true)
        }

        const activeSort = sortModel?.[0] || { field: "updatedAt", sort: "desc" }
        const response = await API.Quotes.FetchPaged({
            part: searchState.part,
            tags: searchState.tags,
            tagsMode: "all",
            page: paginationModel.page,
            limit: paginationModel.pageSize,
            sortField: activeSort.field,
            sortDirection: activeSort.sort,
            filterModel,
        })

        if (response.status === 200) {
            setGridQuotes(response.data?.quotes || [])
            setQuoteCount(response.data?.total || 0)
        } else {
            setGridQuotes([])
            setQuoteCount(0)
            setFeedback((prevData) => ({ ...prevData, open: true, message: response.data?.message || "Failed to load quotes", type: "error" }))
        }

        setLoading(false)
        setGridLoading(false)
    }, [filterModel, paginationModel.page, paginationModel.pageSize, searchState.part, searchState.tags, sortModel])

    const fetchSummaryQuotes = useCallback(async () => {
        const quotesResponse = await API.Quotes.FetchAll()
        setQuotes(Array.isArray(quotesResponse) ? quotesResponse : [])
    }, [])

    const handleOpenUpdateModal = () => selectedRows.length ? setOpenUpdateModal(true) : setFeedback({ open: true, message: "Select at least one record to update!", type: "info" })
    const handleCloseUpdateModal = () => setOpenUpdateModal(false)

    const handleSelectionChange = (selection) => setSelectedRows(selection)

    const handleFilterModelChange = (model) => {
        setPaginationModel((prevModel) => ({ ...prevModel, page: 0 }))
        setFilterModel(model?.items || model?.quickFilterValues ? model : defaultFilterModel)
    }

    const handleUpdateSubmit = async (data) => {
        const { tags } = data
        const formattedTags = tags ? tags.map((tag) => tag.value) : []
        const updatedFields = {}

        Object.keys(data).forEach((key) => {
            if (key === "tags") {
                Object.assign(updatedFields, { [key]: formattedTags })
                return
            }

            if (data[key]) {
                Object.assign(updatedFields, { [key]: data[key] })
            }
        })

        const updatePayload = selectedRows.map((selectedRow) => ({
            _id: selectedRow,
            data: updatedFields,
        }))

        const response = await API.Quotes.UpdateMulti(updatePayload)

        if (response.status === 200) {
            const applyUpdate = (prevQuotes) =>
                prevQuotes.map((quote) => {
                    const update = updatePayload.find((payload) => payload._id === quote._id)
                    return update ? { ...quote, ...update.data, updatedAt: new Date().toISOString() } : quote
                })
            setQuotes(applyUpdate)
            setGridQuotes(applyUpdate)
            fetchQuotePage()

            setFeedback((prevData) => ({ ...prevData, open: true, message: response.data.message, type: "success" }))
        } else {
            setFeedback((prevData) => ({ ...prevData, open: true, message: response?.data?.message || "Error updating quote(s)", type: "error" }))
        }

        setSelectedRows([])
        handleCloseUpdateModal()
    }

    const handleDeleteSubmit = async () => {
        const response = await API.Quotes.Delete(selectedRows)

        if (response.status === 200) {
            setQuotes((prevQuotes) => prevQuotes.filter((quote) => !selectedRows.includes(quote._id)))
            fetchQuotePage()
            setFeedback((prevData) => ({ ...prevData, open: true, message: response.data.message, type: "success" }))
        } else {
            setFeedback((prevData) => ({ ...prevData, open: true, message: response.data.message || "Failed To Delete Quote", type: "error" }))
        }

        setSelectedRows([])
        handleCloseUpdateModal()
    }

    const handlePDF = async () => {
        const res = await API.Quotes.DeliveryWeeklyReport(
            dayjs(defaultStartDate).startOf("day").toDate(),
            dayjs(defaultEndDate).endOf("day").toDate()
        )
        const blob = new Blob([res.data], { type: "application/pdf" })
        const url = window.URL.createObjectURL(blob)

        const a = document.createElement("a")
        a.href = url
        a.download = "Weekly Quotes Report.pdf"
        document.body.appendChild(a)
        a.click()
        a.remove()

        window.URL.revokeObjectURL(url)
    }

    const handleSearch = async () => {
        const part = partNumRef.current.trim()
        const tags = selectedTags.map((tag) => tag.value)
        setPaginationModel((prevModel) => ({ ...prevModel, page: 0 }))
        setSearchState(normalizeSearchState({ part, tags, isActive: Boolean(part || tags.length) }))
    }

    const handleClearSearch = () => {
        partNumRef.current = ""
        setSelectedTags([])
        setPaginationModel((prevModel) => ({ ...prevModel, page: 0 }))
        setSearchState(normalizeSearchState())
    }

    const handleResetToCurrentWeek = () => {
        const currentWeek = getCurrentWeekRange()
        setDefaultStartDate(currentWeek.start.format("YYYY-MM-DD"))
        setDefaultEndDate(currentWeek.end.format("YYYY-MM-DD"))
    }

    const actionButtons = [
        { title: "Update Selected Record(s)", onClick: handleOpenUpdateModal, icon: Construction, color: "primary" },
    ]

    const searchNode = (
        <Box sx={{ width: 500, maxWidth: "100%", mt: 2 }}>
            <Stack spacing={2}>
                <TextField label="Part #" defaultValue="" onChange={(event) => (partNumRef.current = event.target.value)} />
                <Autocomplete
                    multiple
                    options={tagOptions}
                    value={selectedTags}
                    onChange={(event, newValue) => setSelectedTags(newValue)}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => <TextField {...params} label="Tag(s)" />}
                />
                <Button variant={isDarkMode ? "outlined" : "contained"} color="primary" onClick={handleSearch}>Search</Button>
                <Button variant={isDarkMode ? "outlined" : "contained"} color="warning" onClick={handleClearSearch}>Clear Search</Button>
            </Stack>
        </Box>
    )

    const { labels, values, year } = useMemo(() => {
        const currentYear = dayjs().year()
        const endOfYear = dayjs().year(currentYear).endOf("year")
        const weeksInYear = endOfYear.isoWeek()
        const weeklyTotals = Array.from({ length: weeksInYear }, () => 0)

        for (const quote of quotes) {
            if (!quote?.createdAt) continue

            const created = dayjs(quote.createdAt)
            if (created.year() !== currentYear) continue

            const weekIndex = created.isoWeek() - 1
            if (weekIndex < 0 || weekIndex >= weeklyTotals.length) continue

            const quoteTotal = (quote.lines || [])
                .filter((line) => {
                    const type = line.type || line.rowType
                    return type !== "subtotal" && type !== "runningsubtotal"
                })
                .reduce((sum, line) => sum + (Number(line.extPrice) || 0), 0)

            weeklyTotals[weekIndex] += quoteTotal
        }

        const weeklyLabels = Array.from({ length: weeksInYear }, (_, index) => `W${index + 1}`)

        return { labels: weeklyLabels, values: weeklyTotals, year: currentYear }
    }, [quotes])

    const mobileZoomRange = useMemo(() => {
        const totalWeeks = labels.length || 1
        const currentWeekIndex = Math.max(0, Math.min(totalWeeks - 1, dayjs().isoWeek() - 1))
        const startIndex = Math.max(0, currentWeekIndex - 5)
        const endIndex = currentWeekIndex

        return {
            start: (startIndex / totalWeeks) * 100,
            end: ((endIndex + 1) / totalWeeks) * 100,
        }
    }, [labels.length])

    const trendValues = useMemo(() => {
        if (!values.length) return []

        const currentWeekIndex = Math.max(0, Math.min(values.length - 1, dayjs().isoWeek() - 1))
        const windowSize = 4

        return values.map((value, index) => {
            if (index > currentWeekIndex) return null

            const startIndex = Math.max(0, index - (windowSize - 1))
            const window = values
                .slice(startIndex, index + 1)
                .map((entry) => Number(entry) || 0)

            const average = window.reduce((sum, entry) => sum + entry, 0) / window.length
            return Number(average.toFixed(2))
        })
    }, [values])

    const option = useMemo(
        () => ({
            backgroundColor: "transparent",
            title: {
                text: `Weekly Quote Total (${year})`,
                left: "center",
                textStyle: {
                    color: isDarkMode ? "#d7dee7" : "#1f2937",
                },
            },
            tooltip: {
                trigger: "axis",
                valueFormatter: (val) => formatUSD(val),
            },
            grid: {
                left: isMobile ? 44 : 60,
                right: isMobile ? 12 : 20,
                top: isMobile ? 64 : 60,
                bottom: isMobile ? 92 : 60,
                containLabel: true,
            },
            xAxis: {
                type: "category",
                data: labels,
                axisLine: {
                    lineStyle: {
                        color: isDarkMode ? "rgba(143, 180, 255, 0.35)" : "#9ca3af",
                    },
                },
                axisTick: {
                    lineStyle: {
                        color: isDarkMode ? "rgba(143, 180, 255, 0.35)" : "#9ca3af",
                    },
                },
                axisLabel: {
                    interval: isMobile ? 0 : 1,
                    rotate: isMobile ? 35 : 0,
                    fontSize: isMobile ? 10 : 12,
                    color: isDarkMode ? "#a8c4ff" : "#4b5563",
                },
            },
            yAxis: {
                type: "value",
                axisLine: {
                    lineStyle: {
                        color: isDarkMode ? "rgba(143, 180, 255, 0.35)" : "#9ca3af",
                    },
                },
                splitLine: {
                    lineStyle: {
                        color: isDarkMode ? "rgba(143, 180, 255, 0.08)" : "rgba(107, 114, 128, 0.12)",
                    },
                },
                axisLabel: {
                    formatter: (val) => formatUSD(val),
                    fontSize: isMobile ? 10 : 12,
                    color: isDarkMode ? "#a8c4ff" : "#4b5563",
                },
            },
            dataZoom: [
                {
                    type: "inside",
                    start: isMobile ? mobileZoomRange.start : 0,
                    end: isMobile ? mobileZoomRange.end : 100,
                },
                {
                    type: "slider",
                    start: isMobile ? mobileZoomRange.start : 0,
                    end: isMobile ? mobileZoomRange.end : 100,
                    height: isMobile ? 18 : 24,
                    fillerColor: isDarkMode ? "rgba(67, 129, 255, 0.18)" : "rgba(59, 130, 246, 0.14)",
                    borderColor: isDarkMode ? "rgba(110, 168, 254, 0.18)" : "rgba(148, 163, 184, 0.28)",
                    backgroundColor: isDarkMode ? "rgba(18, 28, 39, 0.92)" : "rgba(255, 255, 255, 0.85)",
                    dataBackground: {
                        lineStyle: { color: isDarkMode ? "rgba(143, 180, 255, 0.45)" : "rgba(59, 130, 246, 0.45)" },
                        areaStyle: { color: isDarkMode ? "rgba(67, 129, 255, 0.12)" : "rgba(59, 130, 246, 0.12)" },
                    },
                    textStyle: {
                        color: isDarkMode ? "#a8c4ff" : "#4b5563",
                    },
                    handleStyle: {
                        color: isDarkMode ? "#8fb4ff" : "#3b82f6",
                        borderColor: isDarkMode ? "#8fb4ff" : "#3b82f6",
                    },
                },
            ],
            series: [
                {
                    name: "Total",
                    type: "bar",
                    data: values,
                    barMaxWidth: isMobile ? 16 : 22,
                    itemStyle: {
                        color: isDarkMode ? "rgba(86, 211, 163, 0.72)" : "#3b82f6",
                        borderRadius: [6, 6, 0, 0],
                        borderColor: isDarkMode ? "rgba(110, 255, 196, 0.95)" : "#3b82f6",
                        borderWidth: isDarkMode ? 1.25 : 0,
                        shadowBlur: isDarkMode ? 18 : 0,
                        shadowColor: isDarkMode ? "rgba(86, 211, 163, 0.32)" : "transparent",
                    },
                },
                {
                    name: "Trend",
                    type: "line",
                    data: trendValues,
                    smooth: true,
                    symbol: "none",
                    lineStyle: {
                        width: isMobile ? 2 : 2.5,
                        color: isDarkMode ? "rgba(158, 255, 215, 0.96)" : "#7c3aed",
                        shadowBlur: isDarkMode ? 14 : 0,
                        shadowColor: isDarkMode ? "rgba(86, 211, 163, 0.28)" : "transparent",
                    },
                    z: 3,
                },
            ],
        }),
        [isDarkMode, isMobile, labels, mobileZoomRange.end, mobileZoomRange.start, trendValues, values, year]
    )

    const weeklyQuotes = useMemo(() => {
        const start = dayjs(defaultStartDate).startOf("day")
        const end = dayjs(defaultEndDate).endOf("day")

        return quotes.filter((quote) => {
            if (!quote?.createdAt) return false

            const createdAt = dayjs(quote.createdAt)
            if (!createdAt.isValid()) return false

            return createdAt.valueOf() >= start.valueOf() && createdAt.valueOf() <= end.valueOf()
        })
    }, [quotes, defaultStartDate, defaultEndDate])

    const total = useMemo(() => {
        return weeklyQuotes.reduce((quoteSum, quote) => {
            const linesTotal = (quote.lines || [])
                .filter((line) => line.type !== "subtotal" && line.type !== "runningsubtotal")
                .reduce((sum, line) => sum + (Number(line.extPrice) || 0), 0)

            return quoteSum + linesTotal
        }, 0)
    }, [weeklyQuotes])

    useEffect(() => {
        fetchQuotePage()
    }, [fetchQuotePage])

    useEffect(() => {
        fetchSummaryQuotes()
    }, [fetchSummaryQuotes])

    return (
        <BaseWrapper
            content={
                loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <FormProvider>
                        <Feedback props={feedback} setFeedback={setFeedback} />
                        <RecordModal open={openUpdateModal} handleClose={handleCloseUpdateModal} title="Update Quote(s)" fields={[{ name: "tags", label: "Tags", type: "autocomplete", options: tagOptions, multiple: true }]} onDelete={handleDeleteSubmit} onSubmit={handleUpdateSubmit} isUpdate={true} />

                        <CollapsibleGridCard
                            title="Application Engineering"
                            linkLabel="Quotes"
                            rows={gridQuotes}
                            columns={columns}
                            getRowId={(row) => row._id}
                            checkboxSelection
                            rowSelectionModel={selectedRows}
                            onRowSelectionModelChange={handleSelectionChange}
                            actionButtons={actionButtons}
                            headerRightNode={searchNode}
                            sessionStorageKey={GRID_SESSION_KEYS.engineeringQuotingGrid}
                            sessionUserId={getSessionUserId()}
                            initialPageSize={15}
                            pageSizeOptions={[15, 25, 50]}
                            extraGridProps={{
                                density: "compact",
                                loading: gridLoading,
                                rowCount: quoteCount,
                                paginationMode: "server",
                                sortingMode: "server",
                                filterMode: "server",
                                filterDebounceMs: 400,
                                paginationModel,
                                sortModel,
                                filterModel,
                                onPaginationModelChange: setPaginationModel,
                                onSortModelChange: (model) => {
                                    setPaginationModel((prevModel) => ({ ...prevModel, page: 0 }))
                                    setSortModel(model.length ? model : defaultSortModel)
                                },
                                onFilterModelChange: handleFilterModelChange,
                            }}
                        />

                        <div className="col-xl-12 col-md-12 hive-engineering-quotes-weekly" style={{ marginTop: "1em", marginBottom: "1em" }}>
                            <div className="card">
                                <div
                                    className="card-header d-flex align-items-center justify-content-between hive-engineering-quotes-weekly-header"
                                    style={{ flexWrap: isMobile ? "wrap" : "nowrap", rowGap: isMobile ? "0.75rem" : 0 }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: { xs: "space-between", md: "flex-start" },
                                            width: { xs: "100%", md: "auto" },
                                            minWidth: 0,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, pr: 1 }}>
                                            <IconButton
                                                onClick={() => setCollapseWeekly(!collapsedWeekly)}
                                                size="small"
                                                sx={{ mr: 1 }}
                                                className="hive-engineering-quotes-weekly-toggle"
                                            >
                                                {collapsedWeekly ? <ExpandMore /> : <ExpandLess />}
                                            </IconButton>
                                            <h5 className="hive-engineering-quotes-weekly-title" style={{ margin: 0, lineHeight: 1.2 }}>
                                                Application Engineering - <span style={{ whiteSpace: "nowrap" }}>Quotes By Week</span>
                                            </h5>
                                        </Box>
                                    </Box>
                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={1}
                                        sx={{ mt: 1.5, width: isMobile ? "100%" : "auto", ml: isMobile ? 0 : 4 }}
                                    >
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                label="Start Date"
                                                value={dayjs(defaultStartDate)}
                                                format="MMM D, YYYY"
                                                onChange={(date) => {
                                                    if (date && dayjs(date).isValid()) {
                                                        setDefaultStartDate(dayjs(date).format("YYYY-MM-DD"))
                                                    }
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        className: "hive-engineering-quotes-weekly-date",
                                                        sx: {
                                                            minWidth: { xs: "100%", sm: 180 },
                                                            "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: isDarkMode ? "transparent" : "#fff" },
                                                        },
                                                    },
                                                }}
                                            />
                                            <DatePicker
                                                label="End Date"
                                                value={dayjs(defaultEndDate)}
                                                format="MMM D, YYYY"
                                                onChange={(date) => {
                                                    if (date && dayjs(date).isValid()) {
                                                        setDefaultEndDate(dayjs(date).format("YYYY-MM-DD"))
                                                    }
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        className: "hive-engineering-quotes-weekly-date",
                                                        sx: {
                                                            minWidth: { xs: "100%", sm: 180 },
                                                            "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: isDarkMode ? "transparent" : "#fff" },
                                                        },
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                        <Button variant={isDarkMode ? "outlined" : "outlined"} onClick={handleResetToCurrentWeek}>
                                            Reset to Current Week
                                        </Button>
                                        <Button variant={isDarkMode ? "outlined" : "outlined"} onClick={handlePDF} startIcon={<CloudDownload />}>
                                            Download Report
                                        </Button>
                                    </Stack>
                                </div>
                                <Collapse in={!collapsedWeekly} timeout="auto" unmountOnExit>
                                    <CardContent className="hive-engineering-quotes-weekly-content">
                                        <ReactECharts theme="v5"
                                            className="hive-engineering-quotes-weekly-chart"
                                            ref={chartRef}
                                            option={option}
                                            style={{ height: isMobile ? "380px" : "500px", width: "100%" }}
                                            opts={{ renderer: "svg" }}
                                        />

                                        <Box sx={{ display: "flex", justifyContent: "center", px: 1, mt: 2, mb: 2 }}>
                                            <Box sx={{ width: { xs: "100%", sm: "auto" }, textAlign: "center" }}>
                                                <Typography
                                                    className="hive-engineering-quotes-weekly-total"
                                                    variant={isMobile ? "h6" : "h5"}
                                                    sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                                                >
                                                    Weekly Total: {formatUSD(total)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <DataGridPro
                                            className="hive-engineering-quotes-weekly-grid"
                                            sx={{ backgroundColor: "white" }}
                                            density="compact"
                                            rows={weeklyQuotes}
                                            columns={columns}
                                            getRowId={(row) => row._id}
                                            pagination
                                            initialState={{
                                                pagination: {
                                                    paginationModel: { pageSize: 15, page: 0 },
                                                },
                                            }}
                                            pageSizeOptions={[15, 25, 50]}
                                        />
                                    </CardContent>
                                </Collapse>
                            </div>
                        </div>

                        <DailyQuoteTrendCard quotes={quotes} />
                    </FormProvider>
                )
            }
        />
    )
}

export default Quoting
